import * as React from 'react';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useAtomValue } from 'jotai';
import { errorDataAtom } from '@src/components/metrics-panel-hz/atoms/index';
import { checkInvalidDimension } from '../../utils';
import {
  DEFAULT_RULE_OPERATOR,
  NOT_OPERATOR_DISABLED_TOOLTIP_MESSAGE,
  OPERATOR_SEPARATE_TOOLTIP_MESSAGE,
} from '../../constant';
import * as Store from '../store';
import InvalidDimensionWarning, { CUSTOM_TAG_WARNING_CONTENT } from './InvalidDimensionWarning';
import RuleAtomFieldSelector from './RuleAtomFieldSelector';
import RuleAtomValueSelector, { RuleAtomValueBlock } from './RuleAtomValueSelector';
import * as Styled from './styled';
import type * as Types from '../../../types';

export type RuleAtomChange = Pick<RuleAtomProps, 'op' | 'field' | 'tagKey' | 'values'>;
export interface RuleAtomProps<TPath = [number, number]> {
  op: string;
  field: string;
  /**
   * Force to show the passed label, otherwise, the label will be generated based on the field.
   */
  label?: string;
  tagKey?: string | null;
  values: {
    value: string | number;
    display: string;
  }[];
  path: TPath;
  dimensionReadonly?: boolean;
  operatorReadonly?: boolean;
  valueReadonly?: boolean;
  notOpDisabled?: boolean;
  defaultActive?: boolean;
  forceActive?: boolean;
  forceExpand?: boolean;
  forceShowRelation?: boolean;
  showValidation?: boolean;
  onRemove?: (path: TPath) => void;
  onChange?: (value: RuleAtomChange, path: TPath) => void;
}

const RELATION_DISPLAY = {
  and: '&',
  or: 'or',
};

const OP_DISPLAYS = {
  eq: 'equals',
  not_eq: 'not equals',
  regex: 'match',
  not_regex: 'not match',
  contains: 'contains',
  not_contains: 'not contains',
  starts_with: 'starts with',
  not_starts_with: 'not starts with',
  ends_with: 'ends with',
  not_ends_with: 'not ends with',
};

/**
 * Generate the relation between the current atom and the previous atom.
 * The path is the index of the current atom in the rule group.
 */
function generateRelation(path: [number, number], forceShowRelation?: boolean) {
  const [groupIndex, ruleIndex] = path;

  if (!forceShowRelation && groupIndex === 0 && ruleIndex === 0) return undefined;

  if (ruleIndex === 0) return 'and';

  return 'or';
}

function generateOperatorOptions(
  isCustomTag: boolean,
  metadata?: Types.DimensionFieldMeta,
  notOpDisabled = false,
  multipleValues = false,
) {
  const getNotDisabledLabel = (
    label: React.ReactNode,
    isNot: boolean,
    disableNot: boolean,
    multipleValues: boolean,
  ) => {
    const icon = <InfoCircleOutlined style={{ marginLeft: 4 }} />;

    if (disableNot && isNot) {
      return (
        <>
          {label}
          <Tooltip title={NOT_OPERATOR_DISABLED_TOOLTIP_MESSAGE}>{icon}</Tooltip>
        </>
      );
    }
    if (multipleValues && isNot) {
      return (
        <>
          <Tooltip title={OPERATOR_SEPARATE_TOOLTIP_MESSAGE}>
            {label}
            {icon}
          </Tooltip>
        </>
      );
    }

    return label;
  };

  if (isCustomTag || !metadata)
    return Object.entries(OP_DISPLAYS)
      .filter(([key]) => !key.includes('regex'))
      .map(([key, value]) => {
        return {
          label: getNotDisabledLabel(value, key.startsWith('not_'), notOpDisabled, multipleValues),
          value: key,
          disabled: notOpDisabled && key.startsWith('not_'),
        };
      });

  return (
    metadata?.operators
      .map((it) => {
        return {
          label: getNotDisabledLabel(
            it.name,
            it.value.startsWith('not_'),
            notOpDisabled,
            multipleValues,
          ),
          value: it.value,
          disabled: notOpDisabled && it.value.startsWith('not_'),
        };
      })
      .filter(({ value }) => !value.includes('regex')) || []
  );
}

function generateDimensionStatus(
  metaMap: Map<string, Types.DimensionFieldMeta>,
  field: string,
  isTagKey: boolean,
  disabled?: boolean,
  errorData?: {
    shouldBeReset: boolean;
    fields: string[];
    filters: string[];
  },
) {
  // TAG key is legecy, it's not in the metaMap now.
  // So, we should treat it as a valid field.
  if (isTagKey) {
    return {
      editable: false,
      invalid: true,
    };
  }

  const status = {
    editable: true,
    invalid: false,
  };

  if (!metaMap.size) {
    status.editable = false;
  } else {
    const metadata = metaMap.get(field);
    const isInvalid = checkInvalidDimension(field, errorData?.fields);
    status.editable = !!metadata && !isInvalid;
    status.invalid = !metadata || isInvalid;
  }

  if (disabled) {
    status.editable = false;
  }

  return status;
}

const RuleAtom = (props: RuleAtomProps) => {
  const {
    field,
    tagKey,
    op,
    values,
    path,
    label,
    notOpDisabled,
    defaultActive,
    forceActive,
    forceExpand,
    forceShowRelation,
    operatorReadonly,
    dimensionReadonly,
    valueReadonly,
    showValidation,
    onRemove,
    onChange,
  } = props;
  const errorData = useAtomValue(errorDataAtom);
  const [filterStatus, setFilterStatus] = Store.useFilterStatus();
  const disabled = Store.useFilterDisabled();
  const metaMap = Store.useFilterMetaMap();
  const dimensionLookup = Store.useFilterDimensionLookup();
  const [active, setActive] = React.useState(!!defaultActive);
  const [fieldEditing, setFieldEditing] = React.useState(false);

  const pathRef = React.useRef(path);
  const onRemoveRef = React.useRef(onRemove);
  pathRef.current = path;
  onRemoveRef.current = onRemove;

  const handleRemove = React.useCallback((event) => {
    event.stopPropagation();
    onRemoveRef.current?.(pathRef.current);
  }, []);
  const handleEditActive = React.useCallback(() => {
    setFilterStatus('editing');
    setActive(true);
  }, [setFilterStatus]);

  React.useEffect(() => {
    if (filterStatus !== 'editing') {
      setActive(!!defaultActive);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const metadata = !tagKey ? metaMap.get(field) : undefined;
  const lookupName = !tagKey ? dimensionLookup?.[field] : undefined;
  const dimensionStatus = generateDimensionStatus(metaMap, field, !!tagKey, disabled, errorData);
  const relation = generateRelation(path, forceShowRelation);
  const fieldDisplay = label || metadata?.name || lookupName || tagKey || field;
  const isErrorStatus =
    showValidation && values.length === 1 && values[0].value === '' && values[0].display === '';

  if (fieldEditing) {
    return (
      <RuleAtomFieldSelector
        defaultValue={{ label: fieldDisplay, value: tagKey ?? field }}
        defaultType={tagKey ? 'tag' : 'dimension'}
        status={isErrorStatus ? 'error' : undefined}
        style={{ width: 400 }}
        onCancel={() => setFieldEditing(false)}
        onChange={(value, type) => {
          setFieldEditing(false);
          const fieldOpList = (type === 'dimension' && metaMap.get(value)?.operators) || [];

          onChange?.(
            {
              field: type === 'tag' ? 'TAG' : value,
              tagKey: type === 'tag' ? value : null,
              // Set the default operator based on the field type
              op: fieldOpList[0]?.value || DEFAULT_RULE_OPERATOR,
              // Reset the values when the field is changed
              values: [{ value: '', display: '' }],
            },
            path,
          );
        }}
      />
    );
  }

  const dropdownPanelActive = ['editing-advanced', 'simple-saving'].includes(filterStatus);

  // TODO: it's better to extract the following code to a separate component
  if (forceExpand || forceActive || (filterStatus === 'editing' && active)) {
    const opOptions = generateOperatorOptions(!!tagKey, metadata, notOpDisabled, values.length > 1);

    return (
      <Styled.RuleAtomContainer
        $disabeldHoverStyle={!dimensionStatus.editable}
        $active={forceActive || active}
        $error={isErrorStatus}
        onClick={dimensionStatus.editable ? handleEditActive : undefined}
      >
        {!!relation && <Styled.Relation>{RELATION_DISPLAY[relation]}</Styled.Relation>}
        <Styled.AtomEditableBlock
          $disabled={dimensionReadonly || dropdownPanelActive || !dimensionStatus.editable}
          onClick={() => {
            if (dimensionReadonly || dropdownPanelActive || !dimensionStatus.editable) return;
            setFieldEditing(true);
          }}
        >
          {fieldDisplay}
        </Styled.AtomEditableBlock>
        <Styled.AtomOperatorSelector
          defaultValue={op}
          disabled={operatorReadonly || dropdownPanelActive || !dimensionStatus.editable}
          options={opOptions}
          variant="borderless"
          size="small"
          popupMatchSelectWidth={130}
          onChange={(value) => {
            onChange?.(
              {
                field,
                tagKey,
                op: value,
                values,
              },
              path,
            );
          }}
        />
        <RuleAtomValueSelector
          meta={
            tagKey
              ? metaMap.has('TAG')
                ? { ...metaMap.get('TAG')!, tagKey }
                : undefined
              : metadata
          }
          disabled={valueReadonly || dropdownPanelActive || !dimensionStatus.editable}
          isNot={op.startsWith('not_')}
          value={values}
          onChange={(value) => {
            onChange?.(
              {
                field,
                tagKey,
                op,
                values: value,
              },
              path,
            );
          }}
        />
        {dimensionStatus.invalid && (
          <InvalidDimensionWarning title={tagKey ? CUSTOM_TAG_WARNING_CONTENT : undefined} />
        )}
        {!dropdownPanelActive && (
          <Styled.AtomInnerRemoveButton
            $forceDisplay
            size="small"
            type="text"
            icon={<CloseOutlined />}
            onClick={handleRemove}
          />
        )}
      </Styled.RuleAtomContainer>
    );
  }

  // TODO: it's better to extract the following code to a separate component
  return (
    <Styled.RuleAtomContainer
      // Shouldn't pass `disabled` to `$disabled`, it'll disable all of pointer events,
      // but here need keep the hover effect, so use another prop `$disabeldHoverStyle`.
      $disabled={dropdownPanelActive}
      $disabeldHoverStyle={!dimensionStatus.editable}
      onClick={dimensionStatus.editable ? handleEditActive : undefined}
    >
      {!!relation && <Styled.Relation>{RELATION_DISPLAY[relation]}</Styled.Relation>}
      {op !== DEFAULT_RULE_OPERATOR && <Styled.Operator>{OP_DISPLAYS[op]}</Styled.Operator>}
      <RuleAtomValueBlock disabled value={values} />
      {dimensionStatus.invalid && (
        <InvalidDimensionWarning title={tagKey ? CUSTOM_TAG_WARNING_CONTENT : undefined} />
      )}
      {!dropdownPanelActive && (
        <Styled.AtomInnerRemoveButton
          $forceDisplay={dimensionStatus.invalid}
          size="small"
          type="text"
          icon={<CloseOutlined />}
          onClick={handleRemove}
        />
      )}
    </Styled.RuleAtomContainer>
  );
};

export default React.memo(RuleAtom);
